import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useState } from "react";
import { LayoutDashboard, Users, ClipboardList, BookOpen, LogOut, FileText, UsersRound, TrendingUp, ShieldAlert, Link2, KeyRound, CalendarDays, FolderLock, Activity } from "lucide-react";
import { useAuth } from "@/auth";
import { C } from "@/theme";
import { ChangePasswordModal } from "@/components/ChangePassword";

function navForRole(role) {
  // Core daily work
  const base = [
    { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/roster", label: role === "Rider" ? "My Profile" : "Roster", icon: Users },
    { to: "/sessions", label: "Runs", icon: ClipboardList },
    { to: "/incidents", label: "Incidents", icon: ShieldAlert },
  ];
  // Management & reporting
  if (role === "HOT" || role === "Admin") base.push({ to: "/summary", label: "Summary", icon: TrendingUp });
  if (role === "Admin") {
    base.push({ to: "/assignments", label: "Assignments", icon: Link2 });
    base.push({ to: "/forms", label: "Run Forms", icon: FileText });
    base.push({ to: "/manage-users", label: "Manage Users", icon: UsersRound });
    base.push({ to: "/analytics", label: "App Footfall", icon: Activity });
  }
  // Reference & libraries — kept as the last items so the menu flows better
  base.push({ to: "/guides", label: "Guides", icon: BookOpen });
  // Document Management is available to everyone (contents filtered by folder access rights).
  base.push({ to: "/documents", label: "Documents", icon: FolderLock });
  // Trainers Meeting Area: staff only — never Riders.
  if (role !== "Rider") base.push({ to: "/meetings", label: "Meetings", icon: CalendarDays });
  return base;
}

export function AppShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const items = navForRole(user?.role);
  const [showCp, setShowCp] = useState(false);

  return (
    <div className="flex min-h-screen" style={{ background: C.pageBg }}>
      {/* Sidebar */}
      <aside style={{ background: C.surface }} className="w-64 shrink-0 flex-col hidden md:flex sticky top-0 h-screen">
        <div className="px-5 py-5 border-b flex flex-col gap-2" style={{ borderColor: "rgba(255,255,255,0.1)" }}>
          <img src="/rospa-logo.png" alt="RoSPA Advanced Riders West Midlands" className="w-full max-w-[180px] object-contain" />
          <p className="text-white text-sm font-extrabold font-display tracking-tight" data-testid="sidebar-app-name">WMR Training App</p>
        </div>
        <nav className="flex-1 px-3 py-4 flex flex-col gap-1 overflow-y-auto">
          {items.map((it) => {
            const Icon = it.icon;
            return (
              <NavLink key={it.to} to={it.to} end={it.end} data-testid={`nav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="group" >
                {({ isActive }) => (
                  <div style={{ background: isActive ? C.brandPrimary : "transparent", color: isActive ? "#fff" : C.mutedInverse }}
                    className="flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm font-bold transition-colors hover:bg-white/5">
                    <Icon size={18} />
                    {it.label}
                  </div>
                )}
              </NavLink>
            );
          })}
        </nav>
        <div className="px-3 py-4 border-t" style={{ borderColor: "rgba(255,255,255,0.1)" }}>
          <div className="px-3.5 py-2">
            <p className="text-white text-sm font-bold truncate">{user?.name}</p>
            <p style={{ color: C.mutedInverse }} className="text-[11px]">{user?.role}</p>
          </div>
          <button data-testid="change-password-button" onClick={() => setShowCp(true)}
            style={{ color: C.mutedInverse }} className="w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm font-bold hover:bg-white/5">
            <KeyRound size={18} />Change Password
          </button>
          <button data-testid="logout-button" onClick={() => { logout(); navigate("/login"); }}
            style={{ color: C.mutedInverse }} className="w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm font-bold hover:bg-white/5">
            <LogOut size={18} />Sign Out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 flex flex-col">
        {/* Mobile top bar */}
        <div className="md:hidden flex items-center justify-between px-4 py-3" style={{ background: C.surface }}>
          <img src="/rospa-logo.png" alt="RoSPA" className="h-9 object-contain" />
          <div className="flex items-center gap-4">
            <button data-testid="change-password-button-mobile" onClick={() => setShowCp(true)}><KeyRound size={20} color="#fff" /></button>
            <button data-testid="logout-button-mobile" onClick={() => { logout(); navigate("/login"); }}><LogOut size={20} color="#fff" /></button>
          </div>
        </div>
        {/* Mobile nav strip */}
        <div className="md:hidden flex gap-2 overflow-x-auto px-4 py-2" style={{ background: C.surface }}>
          {items.map((it) => (
            <NavLink key={it.to} to={it.to} end={it.end}>
              {({ isActive }) => (
                <span style={{ background: isActive ? C.brandPrimary : "rgba(255,255,255,0.08)", color: "#fff" }} className="px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap">{it.label}</span>
              )}
            </NavLink>
          ))}
        </div>
        <div key={location.pathname} className="flex-1 p-4 md:p-8 max-w-6xl w-full mx-auto animate-[fadeIn_0.25s_ease]">
          {children}
        </div>
      </main>
      <ChangePasswordModal open={showCp} onClose={() => setShowCp(false)} />
    </div>
  );
}

export function PageHeader({ title, subtitle, action }) {
  return (
    <div className="flex items-start justify-between mb-6 gap-4">
      <div>
        <h1 style={{ color: C.onSurface }} className="text-3xl font-extrabold font-display tracking-tight">{title}</h1>
        {subtitle ? <p style={{ color: C.muted }} className="text-sm mt-1">{subtitle}</p> : null}
      </div>
      {action}
    </div>
  );
}
